/**
 * Context content display component.
 *
 * Displays context content for questions, supporting audio contexts
 * with transcript and audio player, and text contexts.
 *
 * @module ContextContentDisplay
 */

import { FileText, Volume2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { LazyBinaryContent } from '@/components/admin/answersManager/LazyBinaryContent';

interface ContextContentDisplayProps {
  readonly contextType?: string;
  readonly transcript?: string;
  readonly audioBase64?: string;
  readonly mimeType?: string;
  readonly contextContent?: string;
  readonly isEditing?: boolean;
  readonly editingContent?: string;
  readonly onContentChange?: (content: string) => void;
  readonly contextId?: string | number;
  readonly fetchBinaryContent?: (contextId: any) => Promise<string | null>;
}

export default function ContextContentDisplay({
  contextType,
  transcript,
  audioBase64,
  mimeType,
  contextContent,
  isEditing = false,
  editingContent,
  onContentChange,
  contextId,
  fetchBinaryContent,
}: ContextContentDisplayProps) {
  if (isEditing && onContentChange !== undefined) {
    return (
      <Textarea
        value={editingContent || ''}
        onChange={(e) => onContentChange(e.target.value)}
        className="min-h-[100px] text-sm"
        placeholder="Enter context content..."
      />
    );
  }

  if (contextType === 'audio') {
    const hasTranscript = !!transcript;
    const hasInlineAudio = !!(audioBase64 && mimeType);
    const canLazyLoad = !hasInlineAudio && !!contextId && !!fetchBinaryContent;

    if (!hasTranscript && !hasInlineAudio && !canLazyLoad) {
      return (
        <div className="border rounded p-3 bg-muted">
          <p className="font-ui text-xs text-muted-foreground">
            Audio context not available
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {hasTranscript && (
          <div className="border rounded p-3 bg-muted">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="font-ui text-xs font-medium text-foreground">
                Transcript
              </span>
            </div>
            <p className="font-ui text-sm whitespace-pre-wrap">{transcript}</p>
          </div>
        )}
        {hasInlineAudio && (
          <div className="border rounded p-3">
            <div className="flex items-center gap-2 mb-2">
              <Volume2 className="h-4 w-4 text-muted-foreground" />
              <span className="font-ui text-xs font-medium text-foreground">
                Audio
              </span>
            </div>
            <audio
              controls
              src={`data:${mimeType};base64,${audioBase64}`}
              className="w-full"
            >
              <track kind="captions" />
              <span className="font-ui text-sm text-muted-foreground">
                Your browser does not support audio playback
              </span>
            </audio>
          </div>
        )}
        {canLazyLoad && (
          <LazyBinaryContent
            contextId={contextId as number}
            mimeType={mimeType}
            contextType="audio"
            fetchBinaryContent={fetchBinaryContent}
          />
        )}
      </div>
    );
  }

  return (
    <div className="border rounded p-3 bg-muted">
      <p className="font-ui text-sm whitespace-pre-wrap">
        {contextContent || 'No content available'}
      </p>
    </div>
  );
}
